import React, { useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { setNavGuard } from '@/lib/navGuard';

/**
 * Hook that warns users when they try to leave a page with unsaved changes.
 * Handles: browser tab close/refresh, and provides a confirm callback for in-app navigation.
 *
 * @param {boolean} isDirty - Whether there are unsaved changes
 * @param {string} message - Warning message for browser beforeunload
 * @returns {{ confirmNavigation: (callback: () => void) => void, UnsavedDialog: React.FC }}
 */
export function useUnsavedChanges(isDirty, message = 'You have unsaved changes. Are you sure you want to leave?') {
  const [showDialog, setShowDialog] = React.useState(false);
  const pendingCallback = React.useRef(null);

  // Browser tab close / refresh
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, message]);

  // Register dirty state into the global navigation guard so the app-wide
  // <NavigationGuard /> can intercept sidebar/menu clicks and the browser
  // Back button (which beforeunload does not cover for SPA navigation).
  useEffect(() => {
    setNavGuard(isDirty, message);
    return () => setNavGuard(false);
  }, [isDirty, message]);

  // In-app navigation guard
  const confirmNavigation = useCallback((callback) => {
    if (!isDirty) { callback(); return; }
    pendingCallback.current = callback;
    setShowDialog(true);
  }, [isDirty]);

  const handleDiscard = () => {
    setShowDialog(false);
    if (pendingCallback.current) {
      pendingCallback.current();
      pendingCallback.current = null;
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    pendingCallback.current = null;
  };

  const UnsavedDialog = () => (
    <Dialog open={showDialog} onOpenChange={v => { if (!v) handleCancel(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Unsaved Changes
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mt-2">{message}</p>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={handleCancel} className="flex-1 rounded-lg">Keep Editing</Button>
          <Button variant="destructive" onClick={handleDiscard} className="flex-1 rounded-lg" data-testid="discard-changes-btn">Discard & Leave</Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return { confirmNavigation, UnsavedDialog };
}
